import { getMarkerDefinitions, withDefaultEdgeOptions } from '../core';
import type { ConnectionMode, Dimensions, Edge, HandleRef, MarkerDefinition, Node, Rect, Viewport } from '../core';
import type { ConnectionState, InternalNode, MindmapState, NodeTypes } from './types';

/** One node's entry; stable while that node is unchanged, so other nodes' updates don't re-render it. */
export const selectNodeById =
  (id: string) =>
  (state: MindmapState): InternalNode | undefined =>
    state.nodeLookup.get(id);

export const selectNodes = (state: MindmapState): Node[] => state.nodes;

export const selectEdges = (state: MindmapState): Edge[] => state.edges;

export const selectViewport = (state: MindmapState): Viewport => state.viewport;

/** Use with `shallow` equality. */
export const selectZoomLimits = ({ minZoom, maxZoom }: MindmapState) => ({ minZoom, maxZoom });

export const selectNodeTypes = (state: MindmapState): NodeTypes => state.nodeTypes;

/** Ids of rendered nodes in render order (v11 skips hidden nodes). Use with `shallow` equality. */
export const selectVisibleNodeIds = (state: MindmapState): string[] =>
  state.nodes.filter((node) => !node.hidden).map((node) => node.id);

export const selectConnectionMode = (state: MindmapState): ConnectionMode => state.connectionMode;

/** Ids of edges that are not hidden, in render order. Use with `shallow` equality. */
export const selectVisibleEdgeIds = (state: MindmapState): string[] =>
  state.edges.filter((edge) => !edge.hidden).map((edge) => edge.id);

export const selectEdgeById =
  (id: string) =>
  (state: MindmapState): Edge | undefined =>
    state.edgeLookup.get(id);

/** One edge as drawn: its own fields over `defaultEdgeOptions`. Use with `sameDrawnEdge` equality. */
export const selectDrawnEdgeById =
  (id: string) =>
  (state: MindmapState): Edge | undefined => {
    const edge = state.edgeLookup.get(id);
    return edge && withDefaultEdgeOptions(edge, state.defaultEdgeOptions);
  };

/** Equal when neither the edge nor the defaults merged into it changed. */
export const sameDrawnEdge = (a: Edge | undefined, b: Edge | undefined): boolean =>
  a === b || (a !== undefined && b !== undefined && Object.keys({ ...a, ...b }).every((key) => a[key as keyof Edge] === b[key as keyof Edge]));

/** Every marker the visible edges use. Use with `sameMarkers` equality. */
export const selectMarkerDefinitions = (state: MindmapState): MarkerDefinition[] =>
  getMarkerDefinitions(state.edges, state.defaultEdgeOptions);

export const sameMarkers = (a: readonly MarkerDefinition[], b: readonly MarkerDefinition[]): boolean =>
  a.length === b.length && a.every((definition, index) => definition.id === b[index].id);

export const selectNodesDraggable = (state: MindmapState): boolean => state.nodesDraggable;

export const selectConnection = (state: MindmapState): ConnectionState | null => state.connection;

export const selectNodesConnectable = (state: MindmapState): boolean => state.connectOptions.nodesConnectable;

export const selectConnectOnClick = (state: MindmapState): boolean => state.connectOptions.connectOnClick;

export const selectClickConnectStart = (state: MindmapState): HandleRef | null => state.clickConnectStart;

export const selectSelectionRect = (state: MindmapState): Rect | null => state.selectionRect;

/** v11 Controls: the canvas counts as interactive while any of drag, connect or select is on. */
export const selectIsInteractive = (state: MindmapState): boolean =>
  state.nodesDraggable || state.connectOptions.nodesConnectable || state.elementsSelectable;

/** Flow-space boxes of the visible, measured nodes (what the minimap draws). Use with `sameRects`. */
export const selectNodeRects = (state: MindmapState): (Rect & { id: string })[] =>
  Array.from(state.nodeLookup.values()).flatMap(({ node, positionAbsolute, width, height }) =>
    !node.hidden && width && height ? [{ id: node.id, x: positionAbsolute.x, y: positionAbsolute.y, width, height }] : [],
  );

/** Equality for `selectNodeRects`: same ids and boxes in the same order. */
export const sameRects = (a: readonly (Rect & { id: string })[], b: readonly (Rect & { id: string })[]): boolean =>
  a.length === b.length &&
  a.every((rect, index) => {
    const other = b[index];
    return rect.id === other.id && rect.x === other.x && rect.y === other.y && rect.width === other.width && rect.height === other.height;
  });

export const selectPaneSize = (state: MindmapState): Dimensions => state.paneSize;

/** The gesture surface (`.cm-renderer`) overlays like `NodeToolbar` portal into. */
export const selectDomNode = (state: MindmapState): HTMLElement | null => state.domNode;

export const selectSelectedNodeCount = (state: MindmapState): number =>
  state.nodes.reduce((count, node) => (node.selected ? count + 1 : count), 0);
